import { Link } from 'react-router-dom';
import { Download, GraduationCap } from 'lucide-react';
import type { TrainingTaskSummary } from '@hcc/types';
import { Button } from '../ui/Button';
import { CardSection } from '../ui/Card';
import { Pill } from '../ui/Pill';

/**
 * The /me list row for a training-module task. Status comes from the
 * attempt summary: not started, in progress (a draft or failed attempts,
 * with the best score so far), or passed with the certificate download.
 */
export function TrainingTaskRow({
  task,
}: {
  task: {
    task_id: string;
    title: string;
    description: string | null;
    status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
    training?: TrainingTaskSummary | null;
  };
}) {
  const t = task.training;
  const passed = task.status === 'complete' || t?.status === 'passed';
  const inProgress = !passed && t?.status === 'in_progress';
  const label = passed ? 'View' : inProgress ? 'Resume' : 'Start';
  const variant: 'success' | 'warning' | 'ghost' = passed
    ? 'ghost'
    : inProgress
      ? 'warning'
      : 'success';

  return (
    <CardSection>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-fg">
            <GraduationCap className="h-4 w-4 shrink-0 text-fg-muted" />
            {task.title}
          </div>
          {task.description ? (
            <p className="mt-0.5 text-sm text-fg-muted">{task.description}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {passed ? (
            <Pill tone="success">
              {t?.graded === false ? 'Complete' : 'Passed'}
              {t?.best_score != null ? ` · ${t.best_score}%` : ''}
            </Pill>
          ) : inProgress ? (
            <Pill tone="warning">
              In progress{t?.best_score != null ? ` · best ${t.best_score}%` : ''}
            </Pill>
          ) : (
            <Pill tone="neutral">To do</Pill>
          )}
          {passed && t?.certificate_available ? (
            <a
              href={`/api/me/training/${task.task_id}/certificate.pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
            >
              <Download className="h-4 w-4" />
              Certificate
            </a>
          ) : null}
          <Link to={`/me/training/${task.task_id}`}>
            <Button size="sm" variant={variant}>
              {label}
            </Button>
          </Link>
        </div>
      </div>
    </CardSection>
  );
}
